import { Component } from '@theme/component';

/**
 * Tabbed mega menu. Drives the desktop rail and the mobile drawer pill from the
 * same markup contract:
 *   [data-tab-trigger="<id>"]  activates
 *   [data-tab-panel="<id>"]    is shown
 *
 * Activation is declarative: triggers carry `on:click="/selectTab"`, the tablist
 * `on:keydown="/moveTab"` and arrows `on:click="/scrollCards"`, all dispatched by
 * component.js's document-level delegate. Both surfaces switch on click — hover
 * on a rail fights the diagonal move toward a product card.
 *
 * Inactive panels get `inert` rather than `hidden` — the header measures
 * submenu.offsetHeight on open (assets/header-menu.js:211) and a changing height
 * would re-trigger the reveal animation on every switch.
 *
 * @extends {Component}
 */
class MegaMenuTabs extends Component {
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  /** @type {MutationObserver | undefined} */
  #stateObserver;
  /** @type {AbortController | undefined} */
  #scrollController;
  /** @type {string | null} */
  #activeId = null;

  connectedCallback() {
    super.connectedCallback();

    // The header is lazily re-rendered and morphed in after first paint
    // (assets/section-hydration.js). Morph preserves this host but runs
    // copyAttributes over its descendants, and since the server never renders
    // data-tab-active it gets stripped from the live panel — leaving every pane
    // hidden. updatedCallback cannot cover this: morph.js hands onAfterUpdate
    // the incoming DOMParser node, which is never an upgraded Component.
    this.#stateObserver = new MutationObserver(() => this.#restoreActive());
    this.#stateObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-tab-active'],
    });

    this.#restoreActive();

    this.#resizeObserver = new ResizeObserver(() => this.#updateAllArrows());
    this.#observeRows();
    this.#updateAllArrows();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.#stateObserver?.disconnect();
    this.#scrollController?.abort();
  }

  /**
   * Bound via `on:click="/selectTab"`. component.js rewrites event.target to the
   * element carrying the attribute, so this is the trigger itself.
   *
   * @param {Event} event
   */
  selectTab(event) {
    const trigger = event.target;
    if (!(trigger instanceof HTMLElement)) return;

    const id = trigger.dataset.tabTrigger;
    if (!id) return;

    this.#activate(id);
  }

  /**
   * Bound via `on:keydown="/moveTab"` on the tablist. The roles promise arrow-key
   * navigation, and aria-orientation tells assistive tech which keys to expect.
   *
   * @param {Event} event
   */
  moveTab(event) {
    if (!(event instanceof KeyboardEvent)) return;

    const forward = event.key === 'ArrowDown' || event.key === 'ArrowRight';
    const back = event.key === 'ArrowUp' || event.key === 'ArrowLeft';
    if (!forward && !back) return;

    const tabs = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-tab-trigger]')));
    const index = tabs.indexOf(/** @type {HTMLElement} */ (document.activeElement));
    if (index < 0) return;

    event.preventDefault();
    tabs[(index + (forward ? 1 : -1) + tabs.length) % tabs.length]?.focus();
  }

  /**
   * Bound via `on:click="/scrollCards"`.
   *
   * @param {Event} event
   */
  scrollCards(event) {
    const arrow = event.target;
    if (!(arrow instanceof HTMLElement)) return;

    const row = this.#rowFor(arrow);
    if (!row) return;

    const direction = arrow.dataset.scrollDirection === 'prev' ? -1 : 1;
    row.scrollBy({ left: direction * row.clientWidth, behavior: 'smooth' });
  }

  /**
   * The arrow names its row through aria-controls. Falling back to the first row
   * in the panel would be wrong in the drawer, where one pane holds a row per
   * accordion.
   *
   * @param {HTMLElement} arrow
   * @returns {HTMLElement | null}
   */
  #rowFor(arrow) {
    const id = arrow.getAttribute('aria-controls');
    const row = id ? this.querySelector(`#${CSS.escape(id)}`) : null;
    return row instanceof HTMLElement ? row : null;
  }

  /**
   * Terminates naturally: #activate's mutations are delivered as one batch after
   * it returns, by which point a panel is active and this early-returns.
   */
  #restoreActive() {
    if (this.querySelector('[data-tab-panel][data-tab-active]')) return;

    // Derived from the PANELS, so the invariant this guards ("a panel is
    // showing") is the same set the fallback is drawn from. Taking it from the
    // triggers instead would settle silently on an empty menu if the two id
    // sets ever drifted apart.
    const panel = this.querySelector('[data-tab-panel]');
    const fallbackId = panel instanceof HTMLElement ? panel.dataset.tabPanel : undefined;

    // #activeId can outlive its panel if the menu itself changed.
    const preferred =
      this.#activeId && this.querySelector(`[data-tab-panel="${CSS.escape(this.#activeId)}"]`)
        ? this.#activeId
        : fallbackId;

    if (preferred) this.#activate(preferred);
  }

  /** @returns {HTMLElement[]} */
  #cardRows() {
    return /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('[data-panel-cards]')).filter((el) => el instanceof HTMLElement)
    );
  }

  /**
   * Rebound whenever the panels change: a full-page morph in the Theme Editor
   * inserts brand-new panels, whose rows would otherwise never be observed.
   */
  #observeRows() {
    this.#resizeObserver?.disconnect();
    this.#scrollController?.abort();

    const { signal } = (this.#scrollController = new AbortController());

    for (const row of this.#cardRows()) {
      this.#resizeObserver?.observe(row);
      row.addEventListener('scroll', () => this.#updateAllArrows(), { passive: true, signal });
    }
  }

  /** @param {string} id */
  #activate(id) {
    this.#activeId = id;

    for (const el of this.querySelectorAll('[data-tab-trigger]')) {
      if (!(el instanceof HTMLElement)) continue;
      const isActive = el.dataset.tabTrigger === id;
      el.toggleAttribute('data-tab-active', isActive);
      // aria-selected, never aria-expanded: _header-menu.liquid keeps a submenu
      // visible via :has([aria-expanded='true']), so an expanded tab in there
      // would pin its whole submenu open. Every trigger stays in the tab order,
      // with moveTab providing the arrow keys the tab roles promise.
      el.setAttribute('aria-selected', isActive ? 'true' : 'false');
    }

    for (const el of this.querySelectorAll('[data-tab-panel]')) {
      if (!(el instanceof HTMLElement)) continue;
      const isActive = el.dataset.tabPanel === id;
      el.toggleAttribute('data-tab-active', isActive);
      el.inert = !isActive;
    }

    this.#observeRows();
    this.#updateAllArrows();
  }

  #updateAllArrows() {
    for (const row of this.#cardRows()) {
      const panel = row.closest('[data-tab-panel]');
      const arrows = panel?.querySelector('[data-panel-arrows]');
      if (!(arrows instanceof HTMLElement)) continue;

      // Round: sub-pixel layout can leave scrollWidth a fraction over clientWidth.
      arrows.hidden = Math.round(row.scrollWidth) <= Math.round(row.clientWidth);

      // Per direction, so "previous" is not live while the row sits at 0.
      const atStart = Math.round(row.scrollLeft) <= 0;
      const atEnd = Math.round(row.scrollLeft + row.clientWidth) >= Math.round(row.scrollWidth);

      for (const arrow of arrows.querySelectorAll('[data-scroll-direction]')) {
        if (!(arrow instanceof HTMLButtonElement)) continue;
        arrow.disabled = arrow.dataset.scrollDirection === 'prev' ? atStart : atEnd;
      }
    }
  }
}

// header-menu.js refuses to close a submenu while document.activeElement is
// inside it, so a focused rail tab pins the whole mega menu open. Blocking
// mousedown's default keeps focus out without a blur, which would bubble to the
// list item's on:blur and close the menu. Keyboard fires no mousedown.
//
// Match the tab and arrow buttons only, and only inside a tabbed menu. A bare
// [data-scroll-direction] also matches <header-component data-scroll-direction>
// (sections/header.liquid), so closest() resolved to the header host for every
// mousedown anywhere in it — cancelling focus for the cart drawer's inputs and
// any other field the header renders.
function blockPointerFocus() {
  document.addEventListener(
    'mousedown',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const control = target.closest('button[data-tab-trigger], button[data-scroll-direction]');
      if (control?.closest('mega-menu-tabs')) {
        event.preventDefault();
      }
    },
    { capture: true }
  );
}

// header-menu.js animates the header to each submenu's own height, so moving
// between a tabbed menu and a standard one visibly resizes it. Level them all to
// the tallest. Closed submenus are content-visibility: auto and would report the
// contain-intrinsic-size placeholder, so measuring has to force them visible.
function levelSubmenus() {
  const inners = /** @type {HTMLElement[]} */ (Array.from(document.querySelectorAll('.menu-list__submenu-inner')));
  if (inners.length < 2) return;

  const desktop = window.matchMedia('(min-width: 750px)').matches;
  if (!desktop) {
    for (const inner of inners) inner.style.minHeight = '';
    return;
  }

  /** @type {HTMLElement[]} */
  const forced = [];

  for (const inner of inners) {
    inner.style.minHeight = '';
    const submenu = inner.closest('.menu-list__submenu');
    if (submenu instanceof HTMLElement && getComputedStyle(submenu).contentVisibility !== 'visible') {
      submenu.style.contentVisibility = 'visible';
      forced.push(submenu);
    }
  }

  let tallest = 0;
  for (const inner of inners) tallest = Math.max(tallest, inner.offsetHeight);

  for (const inner of inners) inner.style.minHeight = `${tallest}px`;
  for (const submenu of forced) submenu.style.contentVisibility = '';
}

function watchSubmenuHeights() {
  const menu = document.querySelector('.menu-list');
  if (!menu) return;

  // style is server-rendered on the inners, so the hydration morph overwrites it
  // and drops the min-height. Watch attributes as well as content, and pause
  // while writing so our own edits do not retrigger the observer.
  const options = { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] };
  const observer = new MutationObserver(() => schedule());

  let queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      observer.disconnect();
      levelSubmenus();
      observer.observe(menu, options);
    });
  }

  schedule();
  window.addEventListener('resize', schedule, { passive: true });
  observer.observe(menu, options);
}

if (!customElements.get('mega-menu-tabs')) {
  customElements.define('mega-menu-tabs', MegaMenuTabs);
  blockPointerFocus();
  watchSubmenuHeights();
}
